/**
 * Gift card widget state: one pane, no steps. Owns the API calls and the submission
 * sequence (captcha → createPaymentMethod → POST /giftcard → [handleCardAction → re-POST]).
 * Unlike bookings there is NO payment-less path and NO captcha-less endpoint — a gift card
 * IS the payment — so an unusable tenant renders the unavailable notice instead of a form.
 * Framework-agnostic; React binds with useSyncExternalStore.
 */
import type { ApiClient } from '../api/client';
import { ApiError, NetworkError, ProductionGuardError, type FieldErrors } from '../api/errors';
import type {
  GiftcardPurchaseRequest,
  GiftcardPurchaseResponse,
  GiftcardsConfig,
  TenantSettings,
} from '../api/types';
import type { RuntimeConfig } from '../config';
import { createLogger, describeApiFailure, summariseApiFailure, type Logger } from '../diagnostics';
import { GENERIC, customerMessage, scrubFieldErrors, scrubVendorNames } from '../domain/messages';
import { applyStripeKeyOverride } from '../domain/payments';

export interface GiftcardValues {
  amount: number | null;
  recipient_name: string;
  recipient_email: string;
  sender_name: string;
  sender_email: string;
  message: string;
  discountCode: string;
}

export type GiftcardTextField = Exclude<keyof GiftcardValues, 'amount'>;

export type GiftcardStatus =
  'booting' | 'boot-failed' | 'unavailable' | 'ready' | 'submitting' | 'succeeded';

export type UnavailableReason = 'feature' | 'stripe' | 'captcha';

export interface AppliedDiscount {
  code: string;
  percent: number;
  message: string | null;
}

export interface GiftcardState {
  status: GiftcardStatus;
  unavailableReason: UnavailableReason | null;
  settings: TenantSettings | null;
  giftcards: GiftcardsConfig | null;
  values: GiftcardValues;
  discount: { status: 'idle' | 'checking' | 'applied'; applied: AppliedDiscount | null };
  cardComplete: boolean;
  recaptchaToken: string | null;
  /** keys the customer has left (blur) — their validation errors show inline */
  blurred: Record<string, true>;
  /** a submit was attempted — every validation error shows */
  submitted: boolean;
  apiErrors: FieldErrors;
  banner: string[];
  focusKey: string | null;
  submitPhase: 'idle' | 'captcha' | 'payment_method' | 'posting' | 'sca';
  result: GiftcardPurchaseResponse | null;
}

export interface GiftcardControllerDeps {
  client: ApiClient;
  config: RuntimeConfig;
  createPaymentMethod: (billing: { name: string; email: string }) => Promise<string>;
  handleCardAction: (clientSecret: string) => Promise<string>;
  onSuccess?: (result: GiftcardPurchaseResponse, state: GiftcardState) => void;
  /** Diagnostics sink; defaults to the console logger (verbose with ?cbf_debug=1). */
  logger?: Logger;
}

/** Display + focus order of every field the pane can flag. */
export const GIFTCARD_FIELD_ORDER = [
  'amount',
  'recipient_name',
  'recipient_email',
  'sender_name',
  'sender_email',
  'message',
  'discount_code',
  'card',
  'recaptcha_token',
] as const;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isEmail(value: string): boolean {
  return EMAIL_RE.test(value.trim());
}

/**
 * `POST /giftcard` ALWAYS needs a real reCAPTCHA token — the tenant's `recaptcha_disable.giftcard`
 * flag does not relax it. Probed against production 2026-09-29, where that flag is 1 ("off"):
 *   token omitted → 422 "(root): recaptcha_token is required"
 *   token ""      → 422 "String length must be greater than or equal to 1"
 *   token "xxxx…" → 404 (verification failed)
 * And unlike bookings there is no captcha-less variant: the vendor's widget posts gift cards to
 * the same `giftcard` path, from their own domain where their site key is valid. So a gift card
 * can only be bought when a site key valid for THIS domain is configured.
 */
export function giftcardCaptchaRequired(_settings: TenantSettings | null): boolean {
  return true;
}

export function giftcardFinalPrice(amount: number, percent: number): number {
  return Math.round(amount * (100 - percent)) / 100;
}

export interface GiftcardValidationInput {
  values: GiftcardValues;
  amounts: number[];
  cardComplete: boolean;
  captchaRequired: boolean;
  recaptchaToken: string | null;
}

export function validateGiftcard(input: GiftcardValidationInput): FieldErrors {
  const { values } = input;
  const errors: FieldErrors = {};
  if (values.amount === null) errors['amount'] = ['Choose an amount'];
  else if (!input.amounts.includes(values.amount))
    errors['amount'] = ['Choose one of the listed amounts'];
  if (!values.recipient_name.trim()) errors['recipient_name'] = ["Recipient's name is required"];
  if (!values.recipient_email.trim()) errors['recipient_email'] = ["Recipient's email is required"];
  else if (!isEmail(values.recipient_email))
    errors['recipient_email'] = ['Enter a valid email address'];
  if (!values.sender_name.trim()) errors['sender_name'] = ['Your name is required'];
  if (!values.sender_email.trim()) errors['sender_email'] = ['Your email is required'];
  else if (!isEmail(values.sender_email)) errors['sender_email'] = ['Enter a valid email address'];
  if (!values.message.trim()) errors['message'] = ['Add a short message for the recipient'];
  if (!input.cardComplete) errors['card'] = ['Enter your card details'];
  if (input.captchaRequired && !input.recaptchaToken) errors['recaptcha_token'] = [GENERIC.captcha];
  return errors;
}

function emptyValues(): GiftcardValues {
  return {
    amount: null,
    recipient_name: '',
    recipient_email: '',
    sender_name: '',
    sender_email: '',
    message: '',
    discountCode: '',
  };
}

function firstKey(errors: FieldErrors): string | null {
  for (const key of GIFTCARD_FIELD_ORDER) if (errors[key]) return key;
  const leftover = Object.keys(errors)[0];
  return leftover ?? null;
}

export function createGiftcardController(deps: GiftcardControllerDeps) {
  const log = deps.logger ?? createLogger(deps.config.debug);
  let state: GiftcardState = {
    status: 'booting',
    unavailableReason: null,
    settings: null,
    giftcards: null,
    values: emptyValues(),
    discount: { status: 'idle', applied: null },
    cardComplete: false,
    recaptchaToken: null,
    blurred: {},
    submitted: false,
    apiErrors: {},
    banner: [],
    focusKey: null,
    submitPhase: 'idle',
    result: null,
  };
  const listeners = new Set<() => void>();

  function set(patch: Partial<GiftcardState>): GiftcardState {
    state = { ...state, ...patch };
    for (const l of listeners) l();
    return state;
  }

  function clearApiError(...keys: string[]): FieldErrors {
    if (!keys.some((k) => state.apiErrors[k])) return state.apiErrors;
    const apiErrors = { ...state.apiErrors };
    for (const k of keys) delete apiErrors[k];
    return apiErrors;
  }

  function validation(): FieldErrors {
    return validateGiftcard({
      values: state.values,
      amounts: state.giftcards?.amounts ?? [],
      cardComplete: state.cardComplete,
      // The widget only reaches 'ready' with a site key, so the token is always required.
      captchaRequired: giftcardCaptchaRequired(state.settings),
      recaptchaToken: state.recaptchaToken,
    });
  }

  async function start(): Promise<GiftcardState> {
    try {
      const fetched = await deps.client.getSettings();
      // Sandbox answers anonymous requests with an empty body (vendor bug); use the plugin's preload.
      const raw =
        fetched && typeof fetched === 'object' && Object.keys(fetched).length
          ? fetched
          : deps.config.settingsPreload;
      if (!raw) throw new Error('Tenant settings are unavailable');
      const settings = applyStripeKeyOverride(raw, deps.config.stripeKeyOverride, deps.config.env);
      const giftcards = settings.giftcards ?? null;
      const enabled =
        settings.features?.gift_cards !== false &&
        giftcards !== null &&
        (giftcards.amounts ?? []).length > 0;
      if (!enabled)
        return set({ status: 'unavailable', unavailableReason: 'feature', settings, giftcards });
      if (!settings.stripe_public_key)
        return set({ status: 'unavailable', unavailableReason: 'stripe', settings, giftcards });
      // No captcha-less giftcard endpoint exists and the API always demands a verified token, so
      // without a site key the purchase cannot complete — do not take card details we cannot charge.
      if (!deps.config.recaptchaSiteKey) {
        return set({ status: 'unavailable', unavailableReason: 'captcha', settings, giftcards });
      }
      return set({ status: 'ready', settings, giftcards });
    } catch {
      return set({ status: 'boot-failed' });
    }
  }

  function setField(field: GiftcardTextField, value: string): GiftcardState {
    const key = field === 'discountCode' ? 'discount_code' : field;
    const values = { ...state.values, [field]: value };
    // editing the code invalidates a previously applied discount
    const discount =
      field === 'discountCode' && state.discount.applied
        ? { status: 'idle' as const, applied: null }
        : state.discount;
    return set({
      values,
      discount,
      apiErrors: clearApiError(key),
      focusKey: state.focusKey === key ? null : state.focusKey,
    });
  }

  function selectAmount(amount: number): GiftcardState {
    return set({
      values: { ...state.values, amount },
      apiErrors: clearApiError('amount'),
      focusKey: state.focusKey === 'amount' ? null : state.focusKey,
    });
  }

  function blur(key: string): GiftcardState {
    return set({ blurred: { ...state.blurred, [key]: true } });
  }

  function focused(): GiftcardState {
    return state.focusKey ? set({ focusKey: null }) : state;
  }

  function cardState(complete: boolean): GiftcardState {
    return set({
      cardComplete: complete,
      apiErrors: complete ? clearApiError('card') : state.apiErrors,
    });
  }

  function recaptcha(token: string | null): GiftcardState {
    return set({
      recaptchaToken: token,
      apiErrors: token ? clearApiError('recaptcha_token') : state.apiErrors,
    });
  }

  async function applyDiscount(): Promise<GiftcardState> {
    const code = state.values.discountCode.trim();
    if (!code) return state;
    if (!isEmail(state.values.sender_email)) {
      return set({
        apiErrors: {
          ...state.apiErrors,
          discount_code: ['Enter your email above first — the code is checked against it'],
        },
      });
    }
    set({
      discount: { status: 'checking', applied: null },
      apiErrors: clearApiError('discount_code'),
    });
    try {
      const res = await deps.client.checkGiftcardDiscount({
        email: state.values.sender_email.trim(),
        code,
      });
      const percent = typeof res.percent === 'number' ? res.percent : 0;
      return set({
        discount: {
          status: 'applied',
          applied: { code, percent, message: res.message ? scrubVendorNames(res.message) : null },
        },
      });
    } catch (err) {
      const message =
        err instanceof ApiError && err.isValidation
          ? (Object.values(scrubFieldErrors(err.fieldErrors)).flat()[0] ??
            'This discount code is not valid')
          : customerMessage(err, 'The discount code could not be checked. Please try again.');
      return set({
        discount: { status: 'idle', applied: null },
        apiErrors: { ...state.apiErrors, discount_code: [message] },
      });
    }
  }

  function clearDiscount(): GiftcardState {
    return set({
      values: { ...state.values, discountCode: '' },
      discount: { status: 'idle', applied: null },
      apiErrors: clearApiError('discount_code'),
    });
  }

  function buildRequest(
    paymentIntent: { payment_method_id: string } | { payment_intent_id: string },
  ): GiftcardPurchaseRequest {
    const v = state.values;
    const request: GiftcardPurchaseRequest = {
      recipient_name: v.recipient_name.trim(),
      recipient_email: v.recipient_email.trim(),
      sender_name: v.sender_name.trim(),
      sender_email: v.sender_email.trim(),
      message: v.message.trim(),
      amount: v.amount as number,
      discount_code: state.discount.applied?.code ?? null,
      stripe_payment_intent: paymentIntent,
    };
    if (state.recaptchaToken) request.recaptcha_token = state.recaptchaToken;
    return request;
  }

  function fail(apiErrors: FieldErrors, banner: string[]): GiftcardState {
    return set({
      status: 'ready',
      submitPhase: 'idle',
      apiErrors: { ...state.apiErrors, ...apiErrors },
      banner,
      focusKey: firstKey(apiErrors),
    });
  }

  async function submit(): Promise<GiftcardState> {
    if (state.status !== 'ready') return state;
    set({ submitted: true, banner: [], focusKey: null });
    const errors = validation();
    const focusKey = firstKey(errors);
    if (focusKey) return set({ focusKey });

    set({ status: 'submitting', submitPhase: 'payment_method' });
    try {
      const billing = {
        name: state.values.sender_name.trim(),
        email: state.values.sender_email.trim(),
      };
      let paymentMethodId: string;
      try {
        paymentMethodId = await deps.createPaymentMethod(billing);
      } catch (err) {
        log.error('gift card: card verification failed', describeApiFailure(err));
        return fail({ card: [customerMessage(err, GENERIC.card)] }, []);
      }

      set({ submitPhase: 'posting' });
      let result = await deps.client.purchaseGiftcard(
        buildRequest({ payment_method_id: paymentMethodId }),
      );
      if (result.stripe_requires_action && result.stripe_payment_intent_secret) {
        set({ submitPhase: 'sca' });
        let paymentIntentId: string;
        try {
          paymentIntentId = await deps.handleCardAction(result.stripe_payment_intent_secret);
        } catch (err) {
          log.error('gift card: SCA challenge failed', describeApiFailure(err));
          return fail({ card: [customerMessage(err, GENERIC.card)] }, []);
        }
        set({ submitPhase: 'posting' });
        result = await deps.client.purchaseGiftcard(
          buildRequest({ payment_intent_id: paymentIntentId }),
        );
      }

      const done = set({ status: 'succeeded', submitPhase: 'idle', result });
      deps.onSuccess?.(result, done);
      return done;
    } catch (err) {
      if (err instanceof ApiError && err.isValidation) {
        const fieldErrors = scrubFieldErrors(err.fieldErrors);
        const known: FieldErrors = {};
        const banner: string[] = [];
        for (const [key, msgs] of Object.entries(fieldErrors)) {
          if ((GIFTCARD_FIELD_ORDER as readonly string[]).includes(key)) known[key] = msgs;
          else if (key === 'stripe_payment_intent') known['card'] = msgs.map(() => GENERIC.card);
          else banner.push(...msgs);
        }
        if (!Object.keys(known).length && !banner.length)
          banner.push(customerMessage(err, GENERIC.failed));
        log.error(`gift card rejected: ${summariseApiFailure(err, state.submitPhase)}`, {
          ...describeApiFailure(err),
          phase: state.submitPhase,
        });
        log.debug('rejection detail', { fieldErrors: err.fieldErrors, body: err.body });
        return fail(known, banner);
      }
      log.error(`gift card purchase failed: ${summariseApiFailure(err, state.submitPhase)}`, {
        ...describeApiFailure(err),
        phase: state.submitPhase,
      });
      if (err instanceof ApiError && err.isRateLimited) return fail({}, [GENERIC.busy]);
      if (err instanceof NetworkError) return fail({}, [GENERIC.offline]);
      if (err instanceof ProductionGuardError) return fail({}, [GENERIC.failed]);
      return fail({}, [customerMessage(err, GENERIC.failed)]);
    }
  }

  /** Validation errors currently visible for a key (blurred or after a submit attempt). */
  function visibleErrors(key: string): string[] {
    const api = state.apiErrors[key] ?? [];
    if (!state.submitted && !state.blurred[key]) return api;
    return Array.from(new Set([...(validation()[key] ?? []), ...api]));
  }

  return {
    getState: () => state,
    subscribe: (listener: () => void) => (
      listeners.add(listener),
      () => listeners.delete(listener)
    ),
    start,
    setField,
    selectAmount,
    blur,
    focused,
    cardState,
    recaptcha,
    applyDiscount,
    clearDiscount,
    submit,
    visibleErrors,
  };
}

export type GiftcardController = ReturnType<typeof createGiftcardController>;
